import * as Sentry from "@sentry/react"
import posthog from "posthog-js"
import type {
  ObservationDeletionDiagnostics,
  ObservationDeletionOperationCounts,
  ObservationDeletionPreparedDiagnostics,
} from "./deleteObservation"

const STORAGE_KEY = "fieldbook.syncDiagnostics.lastObservationDelete"
const DIAGNOSTIC_TTL_MS = 7 * 24 * 60 * 60 * 1000

type DiagnosticBase = {
  schemaVersion: 1
  appVersion: string
  attemptedAt: string
  observationFingerprint: string
  online: boolean
  followUpUploadAt?: string
}

export type StoredObservationDeleteDiagnostic = DiagnosticBase &
  (
    | { status: "attempted" }
    | {
        status: "prepared"
        prepared: ObservationDeletionPreparedDiagnostics
      }
    | {
        status: "completed"
        completedAt: string
        diagnostics: ObservationDeletionDiagnostics
      }
    | {
        status: "failed"
        errorName: string
        prepared?: ObservationDeletionPreparedDiagnostics
      }
  )

function appVersion(): string {
  return typeof __BUILD_ID__ === "undefined" ? "unknown" : __BUILD_ID__
}

function isOnline(): boolean {
  return typeof navigator === "undefined" ? false : navigator.onLine
}

function fingerprint(observationId: string): string {
  return observationId.slice(-8)
}

function operationCount(counts: ObservationDeletionOperationCounts): number {
  return Object.values(counts).reduce((total, count) => total + count, 0)
}

function persist(diagnostic: StoredObservationDeleteDiagnostic): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(diagnostic))
  } catch {
    // Diagnostics must never interfere with the user's recovery action.
  }
}

function capture(event: string, properties: Record<string, unknown>): void {
  try {
    posthog.capture(event, properties)
  } catch {
    // Telemetry must never interfere with the user's recovery action.
  }
}

function normalizeError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}

function preparedProperties(
  prepared: ObservationDeletionPreparedDiagnostics | undefined,
): Record<string, unknown> {
  if (!prepared) return {}
  return {
    strategy: prepared.strategy,
    queue_total_before: prepared.before.totalQueueOperations,
    related_queue_before: operationCount(
      prepared.before.relatedQueueOperations,
    ),
    related_failures_before: operationCount(
      prepared.before.relatedSyncFailures,
    ),
    association_count: prepared.before.associationCount,
    photo_count: prepared.before.photoCount,
    attachment_count: prepared.before.attachmentCount,
    unrelated_queue_before: prepared.unrelatedQueueOperationsBefore,
  }
}

export function createObservationDeleteTelemetry(observationId: string) {
  const base: DiagnosticBase = {
    schemaVersion: 1,
    appVersion: appVersion(),
    attemptedAt: new Date().toISOString(),
    observationFingerprint: fingerprint(observationId),
    online: isOnline(),
  }
  let prepared: ObservationDeletionPreparedDiagnostics | undefined

  persist({ ...base, status: "attempted" })
  capture("observation_delete_cleanup_attempted", {
    app_version: base.appVersion,
    observation_fingerprint: base.observationFingerprint,
    online: base.online,
  })

  return {
    prepared(diagnostics: ObservationDeletionPreparedDiagnostics): void {
      prepared = diagnostics
      persist({ ...base, status: "prepared", prepared: diagnostics })
    },

    completed(diagnostics: ObservationDeletionDiagnostics): void {
      const followUpUploadAt =
        getLastObservationDeleteDiagnostic()?.followUpUploadAt
      persist({
        ...base,
        status: "completed",
        completedAt: new Date().toISOString(),
        diagnostics,
        followUpUploadAt,
      })
      capture("observation_delete_cleanup_completed", {
        app_version: base.appVersion,
        observation_fingerprint: base.observationFingerprint,
        online: base.online,
        ...preparedProperties(diagnostics),
        queue_total_after: diagnostics.after.totalQueueOperations,
        related_queue_after: operationCount(
          diagnostics.after.relatedQueueOperations,
        ),
        related_failures_after: operationCount(
          diagnostics.after.relatedSyncFailures,
        ),
        queued_server_deletes: operationCount(diagnostics.queuedServerDeletes),
        unexpected_related_after:
          diagnostics.after.unexpectedRelatedOperationCount,
        unrelated_queue_after: diagnostics.unrelatedQueueOperationsAfter,
        unrelated_queue_preserved:
          diagnostics.unrelatedQueueOperationsBefore ===
          diagnostics.unrelatedQueueOperationsAfter,
        duration_ms: diagnostics.durationMs,
      })
    },

    failed(error: unknown): void {
      const normalizedError = normalizeError(error)
      persist({
        ...base,
        status: "failed",
        errorName: normalizedError.name,
        prepared,
      })
      const properties = {
        app_version: base.appVersion,
        observation_fingerprint: base.observationFingerprint,
        online: base.online,
        ...preparedProperties(prepared),
      }
      capture("observation_delete_cleanup_failed", properties)
      try {
        Sentry.captureException(normalizedError, {
          tags: {
            component: "observation_delete_cleanup",
            app_version: base.appVersion,
          },
          contexts: {
            observation_delete: properties,
          },
        })
      } catch {
        // Telemetry must never replace the original deletion error.
      }
    },
  }
}

export function getLastObservationDeleteDiagnostic(): StoredObservationDeleteDiagnostic | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    if (!value) return null
    const parsed = JSON.parse(value) as StoredObservationDeleteDiagnostic
    const attemptedAt = Date.parse(parsed.attemptedAt)
    if (
      !Number.isFinite(attemptedAt) ||
      Date.now() - attemptedAt > DIAGNOSTIC_TTL_MS
    ) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function getLastObservationDeleteDiagnosticContext(): Record<
  string,
  string | number | boolean
> {
  const stored = getLastObservationDeleteDiagnostic()
  if (!stored) return { status: "none" }

  const context: Record<string, string | number | boolean> = {
    status: stored.status,
    app_version: stored.appVersion,
    attempted_at: stored.attemptedAt,
    observation_fingerprint: stored.observationFingerprint,
    online: stored.online,
  }
  if (stored.followUpUploadAt) {
    context.follow_up_upload_at = stored.followUpUploadAt
  }
  const prepared =
    stored.status === "prepared"
      ? stored.prepared
      : stored.status === "failed"
        ? stored.prepared
        : stored.status === "completed"
          ? stored.diagnostics
          : undefined

  if (prepared) {
    Object.assign(context, preparedProperties(prepared))
  }
  if (stored.status === "completed") {
    context.completed_at = stored.completedAt
    context.queue_total_after = stored.diagnostics.after.totalQueueOperations
    context.related_queue_after = operationCount(
      stored.diagnostics.after.relatedQueueOperations,
    )
    context.related_failures_after = operationCount(
      stored.diagnostics.after.relatedSyncFailures,
    )
    context.queued_server_deletes = operationCount(
      stored.diagnostics.queuedServerDeletes,
    )
    context.unexpected_related_after =
      stored.diagnostics.after.unexpectedRelatedOperationCount
    context.unrelated_queue_after =
      stored.diagnostics.unrelatedQueueOperationsAfter
    context.duration_ms = stored.diagnostics.durationMs
  }
  if (stored.status === "failed") {
    context.error_name = stored.errorName
  }
  return context
}

export function recordPowerSyncUploadAfterObservationDelete(args: {
  operationCount: number
  operationSummary: Record<string, number>
}): void {
  const stored = getLastObservationDeleteDiagnostic()
  if (
    !stored ||
    stored.status === "attempted" ||
    stored.status === "failed" ||
    stored.followUpUploadAt
  ) {
    return
  }

  const diagnostics =
    stored.status === "completed" ? stored.diagnostics : stored.prepared

  const followUpUploadAt = new Date().toISOString()
  persist({ ...stored, followUpUploadAt })
  capture("powersync_upload_after_observation_delete_completed", {
    app_version: stored.appVersion,
    observation_fingerprint: stored.observationFingerprint,
    strategy: diagnostics.strategy,
    cleanup_completed_at:
      stored.status === "completed" ? stored.completedAt : "in_progress",
    follow_up_upload_at: followUpUploadAt,
    operation_count: args.operationCount,
    operation_summary: args.operationSummary,
    unrelated_queue_after_cleanup:
      stored.status === "completed"
        ? stored.diagnostics.unrelatedQueueOperationsAfter
        : "pending",
  })
}
